import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const Toster = ({ message, type = "success", duration = 3000, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const animationTimer = setTimeout(() => {
      setIsVisible(true);
    }, 10);

    const closeTimer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => {
        onCloseRef.current?.();
      }, 300);
    }, duration);

    return () => {
      clearTimeout(animationTimer);
      clearTimeout(closeTimer);
    };
  }, [duration, message, type]);

  const getToasterStyles = () => {
    const baseStyles = `
      fixed top-6 right-6 z-[2147483647] 
      min-w-[320px] max-w-[400px] p-4 rounded-xl shadow-2xl
      backdrop-blur-sm border transition-all duration-300 ease-out
      ${isVisible ? "translate-y-0 opacity-100 scale-100" : "-translate-y-8 opacity-0 scale-95"}
    `;

    switch (type) {
      case "success":
        return `${baseStyles} bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 text-green-800 shadow-green-100`;
      case "error":
        return `${baseStyles} bg-gradient-to-r from-red-50 to-rose-50 border-red-200 text-red-800 shadow-red-100`;
      case "warning":
        return `${baseStyles} bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200 text-amber-800 shadow-amber-100`;
      case "info":
        return `${baseStyles} bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200 text-blue-800 shadow-blue-100`;
      default:
        return `${baseStyles} bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200 text-gray-800 shadow-gray-100`;
    }
  };

  const getIcon = () => {
    const iconClass = "w-6 h-6 flex-shrink-0";

    switch (type) {
      case "success":
        return (
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <svg className={`${iconClass} text-green-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case "error":
        return (
          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
            <svg className={`${iconClass} text-red-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        );
      case "warning":
        return (
          <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
            <svg className={`${iconClass} text-amber-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.99-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
        );
      case "info":
        return (
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className={`${iconClass} text-blue-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className={`${iconClass} text-gray-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onCloseRef.current?.(), 300);
  };

  const panel = (
    <div className={getToasterStyles()}>
      <div className="flex items-start gap-3">
        {getIcon()}
        <div className="flex-1 pt-0.5">
          <p className="text-sm font-semibold leading-relaxed">{message}</p>
        </div>
        <button
          type="button"
          onClick={handleClose}
          className="flex-shrink-0 p-1 rounded-full hover:bg-black/5 transition-colors duration-200 group"
        >
          <svg className="w-4 h-4 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );

  if (typeof document === "undefined") return panel;
  return createPortal(panel, document.body);
};

export default Toster;
